"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
const DISPLAY = {
  BLOCK: 'block',
  FLEX: 'flex',
  INLINE: 'inline',
  INLINE_BLOCK: 'inline-block',
  CONTENTS: 'contents'
};
const propTypes = {
  /**
   * The content to render inside the root element
   */
  children: _propTypes.default.node.isRequired,
  /**
   * Callback function that will be invoked when a click event is captured
   */
  onOutsideClick: _propTypes.default.func.isRequired,
  /**
   * If true, the handler will not be invoked
   */
  disabled: _propTypes.default.bool,
  /**
   * Determines the phase at which the outside click handler is invoked during the DOM event flow.
   * If `true`, the handler is invoked during the capturing phase, before the event reaches its target.
   * If `false`, the handler is invoked during the bubbling phase, after the event has reached its target.
   */
  useCapture: _propTypes.default.bool,
  /**
   * The display style of the root element
   */
  display: _propTypes.default.oneOf(Object.values(DISPLAY))
};
const defaultProps = {
  disabled: false,
  // `useCapture` is set to true by default so that a `stopPropagation` in the
  // children will not prevent all outside click handlers from firing
  useCapture: true,
  display: DISPLAY.BLOCK
};
class OutsideClickHandler extends _react.default.Component {
  constructor() {
    super(...arguments);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.setRootNodeRef = this.setRootNodeRef.bind(this);
    this.addMouseDownEventListener = this.addMouseDownEventListener.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
    this.abortController = new AbortController();
  }
  componentDidMount() {
    const {
      disabled,
      useCapture
    } = this.props;
    if (!disabled) this.addMouseDownEventListener(useCapture);
  }
  componentDidUpdate(_ref) {
    let {
      disabled: prevDisabled
    } = _ref;
    const {
      disabled,
      useCapture
    } = this.props;
    if (prevDisabled !== disabled) {
      if (disabled) {
        this.removeEventListeners();
      } else {
        this.addMouseDownEventListener(useCapture);
      }
    }
  }
  componentWillUnmount() {
    this.removeEventListeners();
  }

  // Use mousedown/mouseup to enforce that clicks remain outside the root's
  // descendant tree, even when dragged. This should also get triggered on
  // touch devices.
  onMouseDown(e) {
    const {
      useCapture
    } = this.props;
    const isDescendantOfRoot = this.rootNode && this.rootNode.contains(e.target);
    if (!isDescendantOfRoot) {
      window.addEventListener('mouseup', this.onMouseUp, {
        capture: useCapture,
        signal: this.abortController.signal
      });
    }
  }

  // Use mousedown/mouseup to enforce that clicks remain outside the root's
  // descendant tree, even when dragged. This should also get triggered on
  // touch devices.
  onMouseUp(e) {
    const {
      onOutsideClick
    } = this.props;
    const isDescendantOfRoot = this.rootNode && this.rootNode.contains(e.target);
    if (!isDescendantOfRoot) {
      onOutsideClick(e);
    }
  }
  setRootNodeRef(ref) {
    this.rootNode = ref;
  }
  addMouseDownEventListener(useCapture) {
    // If SSR component, do not add event listeners
    if (typeof document !== 'object') return;
    this.abortController = new AbortController();
    window.addEventListener('mousedown', this.onMouseDown, {
      capture: useCapture,
      signal: this.abortController.signal
    });
  }
  removeEventListeners() {
    this.abortController.abort();
  }
  render() {
    const {
      children,
      display
    } = this.props;
    return /*#__PURE__*/_react.default.createElement("div", {
      ref: this.setRootNodeRef,
      style: display !== DISPLAY.BLOCK && Object.values(DISPLAY).includes(display) ? {
        display
      } : undefined
    }, children);
  }
}
exports.default = OutsideClickHandler;
OutsideClickHandler.propTypes = propTypes;
OutsideClickHandler.defaultProps = defaultProps;